import { useMemo, useState } from "react";
import { useConfirm } from "../confirm-dialog";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { BookOpen, CircleAlert, CircleCheck, Copy, FileUp } from "lucide-react";
import { apiFetch } from "../lib/auth";
import { CreateModal, inputClass, labelClass } from "../admin-ui";
import { TreeNode } from "./tree-node";
import {
  LessonEditor,
  lessonKindIcons,
  lessonKindLabels,
} from "./lesson-editor";
import { lessonIssue, type Lesson, type Module, type Mutate } from "./types";

/** Droppable id for a module's empty lesson list. */
const emptyDropId = (moduleId: string) => `empty:${moduleId}`;

/**
 * Curriculum pane: the module/lesson tree on the left, the selected lesson's
 * editor on the right.
 *
 * A single `DndContext` covers both levels. One context per level would read
 * more tidily, but `useSortable` binds to the nearest context — the lesson
 * lists sit inside the module rows, so a nested module context would capture
 * them and dragging a lesson into a different module could never work.
 */
export function CurriculumTree({
  courseId,
  courseTitle,
  modules,
  mutate,
}: {
  courseId: string;
  courseTitle: string;
  modules: Module[];
  mutate: Mutate;
}) {
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [askConfirm, confirmDialog] = useConfirm();
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState<Module[] | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // The optimistic snapshot survives only while it describes the same set of
  // ids the server does. Anything created or deleted invalidates it and props
  // take over again — no remount needed.
  const view = useMemo(() => {
    if (!optimistic) return modules;
    const fromProps = modules.flatMap((module) => [
      module.id,
      ...module.lessons.map((lesson) => lesson.id),
    ]);
    const fromLocal = new Set(
      optimistic.flatMap((module) => [
        module.id,
        ...module.lessons.map((lesson) => lesson.id),
      ]),
    );
    return fromProps.length === fromLocal.size &&
      fromProps.every((id) => fromLocal.has(id))
      ? optimistic
      : modules;
  }, [modules, optimistic]);

  const selectedLesson = useMemo(
    () =>
      view
        .flatMap((module) => module.lessons)
        .find((lesson) => lesson.id === selectedLessonId) ?? null,
    [view, selectedLessonId],
  );

  const moduleOfLesson = (lessonId: string) =>
    view.find((module) => module.lessons.some((l) => l.id === lessonId));

  /** Wraps a mutation so the tree greys out while the server catches up. */
  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      return await mutate(action, message, courseId);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Shows `next` immediately, then drops it once the request settles so props
   * become authoritative again — on success `mutate` has already refetched, and
   * on failure this is the rollback.
   *
   * Clearing on success matters as much as on failure: the id-set check that
   * guards `view` cannot tell a stale arrangement from a fresh one when only
   * the grouping changed, so a rejected move would otherwise stay on screen.
   */
  const commit = async (
    next: Module[],
    action: () => Promise<unknown>,
    message: string,
  ) => {
    setOptimistic(next);
    try {
      await run(action, message);
    } finally {
      setOptimistic(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (busy || !over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    // ── Module drag ──
    if (view.some((module) => module.id === activeId)) {
      const targetModuleId = view.some((module) => module.id === overId)
        ? overId
        : moduleOfLesson(overId)?.id;
      if (!targetModuleId || targetModuleId === activeId) return;
      const from = view.findIndex((module) => module.id === activeId);
      const to = view.findIndex((module) => module.id === targetModuleId);
      const next = arrayMove(view, from, to);
      await commit(
        next,
        () =>
          apiFetch(`/api/admin/courses/${courseId}/modules/reorder`, {
            method: "POST",
            body: JSON.stringify({ ids: next.map((module) => module.id) }),
          }),
        "Orden actualizado.",
      );
      return;
    }

    // ── Lesson drag ──
    const source = moduleOfLesson(activeId);
    if (!source) return;
    const lesson = source.lessons.find((item) => item.id === activeId);
    if (!lesson) return;

    let targetModuleId: string;
    let targetIndex: number;
    if (overId.startsWith("empty:")) {
      targetModuleId = overId.slice("empty:".length);
      targetIndex = 0;
    } else if (view.some((module) => module.id === overId)) {
      // Dropped on a module row itself: append to the end of that module.
      targetModuleId = overId;
      targetIndex =
        view.find((module) => module.id === overId)?.lessons.length ?? 0;
    } else {
      const target = moduleOfLesson(overId);
      if (!target) return;
      targetModuleId = target.id;
      targetIndex = target.lessons.findIndex((item) => item.id === overId);
    }

    if (targetModuleId === source.id) {
      const from = source.lessons.findIndex((item) => item.id === activeId);
      if (from === targetIndex) return;
      const reordered = arrayMove(source.lessons, from, targetIndex);
      await commit(
        view.map((module) =>
          module.id === source.id ? { ...module, lessons: reordered } : module,
        ),
        () =>
          apiFetch(`/api/admin/modules/${source.id}/lessons/reorder`, {
            method: "POST",
            body: JSON.stringify({ ids: reordered.map((item) => item.id) }),
          }),
        "Orden actualizado.",
      );
      return;
    }

    await commit(
      view.map((module) => {
        if (module.id === source.id)
          return {
            ...module,
            lessons: module.lessons.filter((item) => item.id !== activeId),
          };
        if (module.id === targetModuleId) {
          const lessons = module.lessons.filter((item) => item.id !== activeId);
          lessons.splice(Math.min(targetIndex, lessons.length), 0, lesson);
          return { ...module, lessons };
        }
        return module;
      }),
      () =>
        apiFetch(`/api/admin/lessons/${activeId}/move`, {
          method: "POST",
          body: JSON.stringify({
            moduleId: targetModuleId,
            position: targetIndex,
          }),
        }),
      "Clase movida.",
    );
  };

  const renameModule = (id: string, title: string) =>
    run(
      () =>
        apiFetch(`/api/admin/modules/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ title }),
        }),
      "Módulo guardado.",
    );

  const renameLesson = (id: string, title: string) =>
    run(
      () =>
        apiFetch(`/api/admin/lessons/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ title }),
        }),
      "Clase guardada.",
    );

  const lessonCount = view.reduce(
    (total, module) => total + module.lessons.length,
    0,
  );

  return (
    <div className="border-ink bg-paper border-surface p-5">
      {confirmDialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-extrabold">Programa</h2>
        <div className="flex items-center gap-3">
          <p className="text-ink-soft font-mono text-xs uppercase">
            {view.length} módulos · {lessonCount} clases
          </p>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="border-ink bg-paper font-display border-chip flex items-center gap-1 px-2 py-1 text-xs font-bold"
          >
            <FileUp size={13} /> Importar
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <TreeNode<Module>
            contextless
            items={view}
            label="Módulos"
            addLabel="Módulo"
            emptyLabel="Todavía no hay módulos. Crea el primero."
            disabled={busy}
            onCreate={() =>
              run(
                () =>
                  apiFetch(`/api/admin/courses/${courseId}/modules`, {
                    method: "POST",
                    body: JSON.stringify({
                      title: `Módulo ${view.length + 1}`,
                    }),
                  }),
                "Módulo creado.",
              )
            }
            onRename={renameModule}
            itemAction={(module) => ({
              label: `Duplicar ${module.title}`,
              icon: <Copy size={14} />,
              run: () =>
                run(
                  () =>
                    apiFetch(`/api/admin/modules/${module.id}/duplicate`, {
                      method: "POST",
                    }),
                  "Módulo duplicado.",
                ),
            })}
            onDelete={async (id) => {
              const target = view.find((item) => item.id === id);
              const count = target?.lessons.length ?? 0;
              const sure = await askConfirm({
                title: target
                  ? `¿Eliminar el módulo «${target.title}»?`
                  : "¿Eliminar este módulo?",
                message: count
                  ? `Se borran sus ${count} ${count === 1 ? "clase" : "clases"} con sus videos, materiales, quizzes y comentarios. Esta acción no se puede deshacer.`
                  : "El módulo está vacío. Esta acción no se puede deshacer.",
                confirmLabel: "Eliminar módulo",
                destructive: true,
              });
              if (!sure) return;
              run(
                () =>
                  apiFetch(`/api/admin/modules/${id}`, { method: "DELETE" }),
                "Módulo eliminado.",
              );
            }}
            childComponent={(module) => (
              <TreeNode<Lesson>
                contextless
                depth={1}
                emptyDroppableId={emptyDropId(module.id)}
                items={module.lessons}
                label="Clases"
                addLabel="Clase"
                emptyLabel="Sin clases. Arrastra una aquí o crea la primera."
                disabled={busy}
                selectedId={selectedLessonId ?? undefined}
                onSelect={(lesson) => setSelectedLessonId(lesson.id)}
                itemIcon={(lesson) => {
                  const Icon = lessonKindIcons[lesson.kind];
                  return (
                    <Icon
                      size={14}
                      aria-label={lessonKindLabels[lesson.kind]}
                    />
                  );
                }}
                itemBadge={(lesson) => {
                  const issue = lessonIssue(lesson);
                  return issue ? (
                    <span
                      title={issue}
                      className="text-ink-soft flex items-center"
                    >
                      <CircleAlert size={14} />
                    </span>
                  ) : lesson.published ? (
                    <span title="Publicada" className="flex items-center">
                      <CircleCheck size={14} />
                    </span>
                  ) : null;
                }}
                onCreate={() =>
                  run(
                    () =>
                      apiFetch(`/api/admin/modules/${module.id}/lessons`, {
                        method: "POST",
                        body: JSON.stringify({
                          title: `Clase ${module.lessons.length + 1}`,
                          content: "",
                          durationMinutes: 0,
                          published: false,
                          kind: "video",
                        }),
                      }),
                    "Clase creada.",
                  )
                }
                onRename={renameLesson}
                onDelete={async (id) => {
                  const lesson = view
                    .flatMap((item) => item.lessons)
                    .find((item) => item.id === id);
                  const sure = await askConfirm({
                    title: lesson
                      ? `¿Eliminar la clase «${lesson.title}»?`
                      : "¿Eliminar esta clase?",
                    message:
                      "Se borran su video, materiales, quiz, comentarios y el progreso que las personas tenían en ella. Esta acción no se puede deshacer.",
                    confirmLabel: "Eliminar clase",
                    destructive: true,
                  });
                  if (!sure) return;
                  if (id === selectedLessonId) setSelectedLessonId(null);
                  run(
                    () =>
                      apiFetch(`/api/admin/lessons/${id}`, {
                        method: "DELETE",
                      }),
                    "Clase eliminada.",
                  );
                }}
              />
            )}
          />
        </DndContext>

        {selectedLesson ? (
          <LessonEditor
            key={selectedLesson.id}
            lesson={selectedLesson}
            courseId={courseId}
            mutate={mutate}
          />
        ) : (
          <div className="border-ink bg-bone border-surface h-fit p-10 text-center">
            <BookOpen className="mx-auto" size={38} />
            <p className="font-display mt-3 text-lg font-bold">
              Elige una clase de {courseTitle} para editarla.
            </p>
            <p className="text-ink-soft mt-2 text-sm">
              Arrastra desde el ⠿ para reordenar, incluso de un módulo a otro.
              Doble clic en el nombre para renombrar.
            </p>
          </div>
        )}
      </div>

      <CreateModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Importar programa"
        description="Pega el sílabus en Markdown. Todo entra como borrador."
      >
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            const markdown = String(
              new FormData(event.currentTarget).get("markdown"),
            );
            const saved = await run(
              () =>
                apiFetch(`/api/admin/courses/${courseId}/import`, {
                  method: "POST",
                  body: JSON.stringify({ markdown }),
                }),
              "Programa importado.",
            );
            if (saved) setImportOpen(false);
          }}
          className="space-y-3"
        >
          <label className={labelClass}>
            Markdown
            <textarea
              autoFocus
              name="markdown"
              required
              rows={12}
              placeholder={
                "## Módulo 1 — Preparar el kit\n- Bienvenida (video)\n- Chequeo rápido (quiz)"
              }
              className={`${inputClass} font-mono`}
            />
          </label>
          <p className="text-ink-soft text-sm">
            <strong>##</strong> abre un módulo, <strong>-</strong> agrega una
            clase. Termina con (video), (artículo), (quiz) o (proyecto) para
            elegir el tipo.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setImportOpen(false)}
              className="border-ink bg-paper font-display border-chip px-4 py-2 text-sm font-bold"
            >
              Cancelar
            </button>
            <button className="border-ink bg-acid font-display shadow-brutal border-chip px-4 py-2 text-sm font-bold">
              Importar
            </button>
          </div>
        </form>
      </CreateModal>
    </div>
  );
}
